import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { DateRangePicker } from '@/components/analytics/date-range-picker';
import { Loader } from '@/components/loader';
import {
  errorMessage,
  tokenApi,
  type Overview,
  type SeriesPoint,
  type TenantOption,
  type UsageGroup,
  type UsageRow,
} from './api';
import { timeAgo } from './format';
import { ProviderCell, TenantCell, TenantSelect } from './shared';
import { useTenantOptions } from './hooks';

const GROUPS: { id: UsageGroup; label: string }[] = [
  { id: 'tenant', label: 'Tenants' },
  { id: 'user', label: 'Users' },
  { id: 'provider', label: 'Providers' },
];

const CARD = 'rounded-2xl border border-slate-200/70 dark:border-slate-700/60 bg-white dark:bg-slate-900';
const COLORS = { issued: 'bg-sky-500', refreshed: 'bg-emerald-500', failed: 'bg-rose-500' } as const;

const number = (n: number) => n.toLocaleString();

/* ── Health banner ─────────────────────────────────────────────────────── */

const Stat = ({ label, value, dot, hint }: { label: string; value: number; dot?: string; hint: string }) => (
  <div className="min-w-0" title={hint}>
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
      {dot && <span className={`h-2 w-2 rounded-full ${dot}`} />}
      {label}
    </div>
    <p className="mt-1 text-2xl font-bold tabular-nums text-slate-800 dark:text-slate-100">{number(value)}</p>
  </div>
);

const HealthBanner = ({ overview }: { overview: Overview }) => {
  const { issued, refreshed, failed, revoked } = overview.totals;
  const ok = issued + refreshed;
  const total = ok + failed;
  const rate = total ? (ok / total) * 100 : null;
  const tone =
    rate === null
      ? { text: 'text-slate-500', label: 'No activity yet' }
      : rate >= 99
        ? { text: 'text-emerald-500', label: 'Healthy' }
        : rate >= 95
          ? { text: 'text-amber-500', label: 'Degraded' }
          : { text: 'text-rose-500', label: 'Needs attention' };

  return (
    <section className={`${CARD} p-6 grid gap-6 lg:grid-cols-[minmax(0,260px)_1fr] items-center`}>
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Success rate</p>
        <p className={`mt-1 text-5xl font-bold tabular-nums ${tone.text}`}>
          {rate === null ? '—' : `${rate.toFixed(rate === 100 ? 0 : 1)}%`}
        </p>
        <p className="mt-1 text-sm text-slate-500">
          {tone.label}
          {total > 0 && ` · ${number(total)} requests`}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3 lg:grid-cols-5 lg:border-l lg:border-slate-100 lg:dark:border-slate-800 lg:pl-6">
        <Stat label="Live sessions" value={overview.live_sessions} hint="Sessions not revoked and still inside their idle and maximum age windows right now, across all tenants." />
        <Stat label="Issued" value={issued} dot={COLORS.issued} hint="Platform tokens issued from a fresh sign-in or application launch." />
        <Stat label="Refreshed" value={refreshed} dot={COLORS.refreshed} hint="Tokens renewed with a refresh token. Each one extends the session." />
        <Stat label="Failed" value={failed} dot={COLORS.failed} hint="Token issues and refreshes that were rejected, including expired and revoked sessions." />
        <Stat label="Revoked" value={revoked} hint="Sessions ended by an administrator." />
      </div>
    </section>
  );
};

/* ── Activity chart ────────────────────────────────────────────────────── */

const niceMax = (value: number) => {
  if (value <= 4) return 4;
  const pow = 10 ** Math.floor(Math.log10(value));
  return Math.ceil(value / pow) * pow;
};

const ActivityChart = ({ series }: { series: SeriesPoint[] }) => {
  const [hover, setHover] = useState<number | null>(null);
  const max = niceMax(Math.max(...series.map((p) => p.issued + p.refreshed + p.failed), 1));
  const ticks = [1, 0.75, 0.5, 0.25, 0].map((f) => Math.round(max * f));
  const labelEvery = Math.max(1, Math.ceil(series.length / 8));
  const active = hover !== null ? series[hover] : null;

  return (
    <section className={`${CARD} p-5`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">Token activity</h3>
        <div className="flex items-center gap-4 text-xs text-slate-500">
          {(['issued', 'refreshed', 'failed'] as const).map((k) => (
            <span key={k} className="flex items-center gap-1.5 capitalize">
              <span className={`h-2 w-2 rounded-full ${COLORS[k]}`} />
              {k}
            </span>
          ))}
        </div>
      </div>

      {series.length === 0 ? (
        <div className="py-20 text-center text-sm text-slate-500">No token activity in this period</div>
      ) : (
        <div className="mt-4 flex gap-3">
          <div className="flex h-56 flex-col justify-between text-[10px] font-mono text-slate-400 tabular-nums">
            {ticks.map((t, i) => (
              <span key={i} className="leading-none">{number(t)}</span>
            ))}
          </div>
          <div className="relative flex-1">
            <div className="pointer-events-none absolute inset-x-0 top-0 flex h-56 flex-col justify-between">
              {ticks.map((_, i) => (
                <div key={i} className="border-t border-dashed border-slate-100 dark:border-slate-800" />
              ))}
            </div>

            <div className="relative flex h-56 items-end gap-1" onMouseLeave={() => setHover(null)}>
              {series.map((p, i) => (
                <div
                  key={p.day}
                  className="relative flex h-full flex-1 items-end"
                  onMouseEnter={() => setHover(i)}
                >
                  <div
                    className={`flex w-full flex-col-reverse overflow-hidden rounded-t transition-opacity ${hover !== null && hover !== i ? 'opacity-40' : ''}`}
                    style={{ height: `${((p.issued + p.refreshed + p.failed) / max) * 100}%` }}
                  >
                    <div className={COLORS.issued} style={{ flex: p.issued }} />
                    <div className={COLORS.refreshed} style={{ flex: p.refreshed }} />
                    <div className={COLORS.failed} style={{ flex: p.failed }} />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-2 flex gap-1">
              {series.map((p, i) => (
                <span key={p.day} className="flex-1 text-center text-[10px] font-mono text-slate-400">
                  {i % labelEvery === 0 ? p.day.slice(5) : ''}
                </span>
              ))}
            </div>

            {active && (
              <div
                className="pointer-events-none absolute top-0 z-10 w-40 -translate-x-1/2 rounded-lg border border-slate-200 bg-white p-2.5 text-xs shadow-lg dark:border-slate-700 dark:bg-slate-800"
                style={{ left: `${Math.min(88, Math.max(12, ((hover! + 0.5) / series.length) * 100))}%` }}
              >
                <p className="mb-1 font-mono font-semibold text-slate-700 dark:text-slate-200">{active.day}</p>
                {(['issued', 'refreshed', 'failed'] as const).map((k) => (
                  <p key={k} className="flex items-center justify-between capitalize text-slate-500">
                    <span className="flex items-center gap-1.5">
                      <span className={`h-2 w-2 rounded-full ${COLORS[k]}`} />
                      {k}
                    </span>
                    <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">{number(active[k])}</span>
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

/* ── Failure reasons ───────────────────────────────────────────────────── */

const FailureReasons = ({ reasons }: { reasons: Overview['failure_reasons'] }) => {
  const top = Math.max(...reasons.map((r) => r.count), 1);
  return (
    <section className={`${CARD} p-5`}>
      <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">Why requests fail</h3>
      {reasons.length === 0 ? (
        <p className="py-16 text-center text-sm text-slate-500">No failures in this period</p>
      ) : (
        <ul className="mt-4 space-y-4">
          {reasons.map((r) => (
            <li key={r.reason}>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="truncate text-slate-700 dark:text-slate-200" title={r.reason}>{r.reason}</span>
                <span className="font-mono font-bold tabular-nums text-slate-800 dark:text-slate-100">{number(r.count)}</span>
              </div>
              <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800">
                <div className="h-full rounded-full bg-rose-500" style={{ width: `${(r.count / top) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

/* ── Top usage ─────────────────────────────────────────────────────────── */

const TopUsage = ({
  usage,
  tenants,
}: {
  usage: Partial<Record<UsageGroup, UsageRow[]>>;
  tenants: TenantOption[];
}) => {
  const [group, setGroup] = useState<UsageGroup>('tenant');
  const rows = usage[group] ?? [];
  const top = Math.max(...rows.map((r) => r.issued + r.refreshed), 1);

  return (
    <section className={`${CARD} overflow-hidden`}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-3 dark:border-slate-800">
        <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">Who is using tokens</h3>
        <div className="inline-flex rounded-lg bg-slate-100 p-0.5 dark:bg-slate-800" role="tablist">
          {GROUPS.map((g) => (
            <button
              key={g.id}
              role="tab"
              aria-selected={group === g.id}
              onClick={() => setGroup(g.id)}
              className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                group === g.id
                  ? 'bg-white text-slate-800 shadow-sm dark:bg-slate-700 dark:text-slate-100'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-slate-500">No data available</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-slate-400">
                <th className="px-5 py-2 font-semibold">{GROUPS.find((g) => g.id === group)?.label.replace(/s$/, '')}</th>
                <th className="px-3 py-2 font-semibold w-1/3">Volume</th>
                <th className="px-3 py-2 text-right font-semibold">Issued</th>
                <th className="px-3 py-2 text-right font-semibold">Refreshed</th>
                <th className="px-3 py-2 text-right font-semibold">Failed</th>
                <th className="px-5 py-2 text-right font-semibold">Last seen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((r) => {
                return (
                  <tr key={r.key}>
                    <td className="max-w-xs px-5 py-3 font-semibold text-slate-700 dark:text-slate-200">
                      {group === 'tenant' ? (
                        <TenantCell slug={r.key} tenants={tenants} />
                      ) : group === 'provider' ? (
                        <ProviderCell provider={r.key} />
                      ) : (
                        <span className="block truncate" title={r.key}>{r.key}</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-800">
                        <div className="h-full rounded-full bg-sky-500" style={{ width: `${((r.issued + r.refreshed) / top) * 100}%` }} />
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right font-mono tabular-nums text-slate-700 dark:text-slate-200">{number(r.issued)}</td>
                    <td className="px-3 py-3 text-right font-mono tabular-nums text-slate-700 dark:text-slate-200">{number(r.refreshed)}</td>
                    <td className={`px-3 py-3 text-right font-mono tabular-nums ${r.failed ? 'font-bold text-rose-500' : 'text-slate-400'}`}>{number(r.failed)}</td>
                    <td className="px-5 py-3 text-right text-xs text-slate-500">{timeAgo(r.last_seen)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

/* ── Page ──────────────────────────────────────────────────────────────── */

export const OverviewTab = () => {
  const [days, setDays] = useState(30);
  const [tenant, setTenant] = useState('');
  const tenants = useTenantOptions();
  const [loaded, setLoaded] = useState<{
    key: string;
    overview: Overview;
    usage: Partial<Record<UsageGroup, UsageRow[]>>;
  } | null>(null);
  const key = `${days}|${tenant}`;
  const loading = loaded?.key !== key;

  useEffect(() => {
    let cancelled = false;
    Promise.all([tokenApi.overview(days, tenant), ...GROUPS.map((g) => tokenApi.usage(g.id, days, tenant))])
      .then(([summary, ...rows]) => {
        if (cancelled) return;
        setLoaded({ key, overview: summary, usage: Object.fromEntries(GROUPS.map((g, i) => [g.id, rows[i]])) });
      })
      .catch((err) => !cancelled && toast.error(errorMessage(err, 'Failed to load token overview')));
    return () => {
      cancelled = true;
    };
  }, [days, tenant, key]);

  const reasons = useMemo(() => loaded?.overview.failure_reasons ?? [], [loaded]);

  if (!loaded) {
    return (
      <div className="py-24 flex justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className={`space-y-6 transition-opacity ${loading ? 'opacity-60' : ''}`} aria-busy={loading}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <TenantSelect value={tenant} onChange={setTenant} tenants={tenants} />
        <DateRangePicker value={days} onChange={setDays} />
      </div>

      <HealthBanner overview={loaded.overview} />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <ActivityChart series={loaded.overview.series} />
        </div>
        <FailureReasons reasons={reasons} />
      </div>

      <TopUsage usage={loaded.usage} tenants={tenants} />
    </div>
  );
};
